// Dismissible-tips persistence (T5).
//
// Storage: browser localStorage, per-user, two independent stores:
//   - `dismissed_tips_${sub}`  → JSON array of dismissed tip ids
//   - `tutorial_done_${sub}`   → 'true' once the first-run tutorial (T7) has
//                                been completed or skipped
//
// Decision (see docs/plans/2026-07-24-ui-tweaks.md, T5): localStorage, not a
// backend field — zero backend surface, instant, no roundtrip. Cost:
// dismissals/tutorial state don't roam across devices/browsers.
//
// UPGRADE PATH: when this needs to roam across devices, swap the bodies of
// the functions below to read/write a `PROFILE.dismissedTips` (and
// `PROFILE.tutorialDone`) field via the backend API instead of localStorage.
// Every function here is already async-shaped at the call site in spirit
// (callers don't inspect return values synchronously in the render path
// beyond the initial mount), so call sites in Dismissible.js / App.js /
// WelcomeTour.js should NOT need to change — only the implementations below.
//
// Identity: `sub` here is the per-user partition key. It is passed in by the
// caller rather than fetched inside this module (see call sites) — this repo's
// existing convention for namespacing localStorage per-user is `user.username`
// from the Authenticator's `user` render prop (see `sources_${user.username}`
// in NewsSourceManager.js/PodcastPlayer.js/ScheduleWizard.js and
// `podcasts_${user.username}` in PodcastPlayer.js). A separate, async-only
// Cognito `sub` claim (`fetchAuthSession().tokens.idToken.payload.sub`) exists
// in services/scheduleService.js but is used solely for backend auth calls,
// not for localStorage namespacing — so `user.username` is what call sites
// should pass in as the `sub` argument here, matching every other
// localStorage key in the app.

const DISMISSED_TIPS_PREFIX = 'dismissed_tips_';
const TUTORIAL_DONE_PREFIX = 'tutorial_done_';

// Fired on `window` whenever a user's dismissals are cleared via restoreAll().
// Dismissible.js listens for this so already-mounted tips reappear without a
// full page reload.
export const TIPS_RESTORED_EVENT = 'linguanewscast:tips-restored';

function dismissedTipsKey(sub) {
  return `${DISMISSED_TIPS_PREFIX}${sub}`;
}

function tutorialDoneKey(sub) {
  return `${TUTORIAL_DONE_PREFIX}${sub}`;
}

function readDismissedIds(sub) {
  if (!sub) return [];
  try {
    const raw = localStorage.getItem(dismissedTipsKey(sub));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('dismissals: failed to read dismissed tips', error);
    return [];
  }
}

/** Has this user already dismissed tip `id`? */
export function isDismissed(sub, id) {
  if (!sub || !id) return false;
  return readDismissedIds(sub).includes(id);
}

/** Persist that this user dismissed tip `id`. */
export function dismiss(sub, id) {
  if (!sub || !id) return;
  const current = readDismissedIds(sub);
  if (current.includes(id)) return;
  try {
    localStorage.setItem(dismissedTipsKey(sub), JSON.stringify([...current, id]));
  } catch (error) {
    console.error('dismissals: failed to persist dismissed tip', error);
  }
}

/** Clear all dismissed tips for this user ("Restore tips" footer link). */
export function restoreAll(sub) {
  if (!sub) return;
  try {
    localStorage.removeItem(dismissedTipsKey(sub));
  } catch (error) {
    console.error('dismissals: failed to restore tips', error);
  }
  window.dispatchEvent(new CustomEvent(TIPS_RESTORED_EVENT, { detail: { sub } }));
}

// --- Tutorial-done flag (T7 consumes these; built now so the util is complete) ---

/** Has this user finished (or skipped) the first-run welcome tour? */
export function isTutorialDone(sub) {
  if (!sub) return false;
  try {
    return localStorage.getItem(tutorialDoneKey(sub)) === 'true';
  } catch (error) {
    console.error('dismissals: failed to read tutorial flag', error);
    return false;
  }
}

/** Mark the first-run welcome tour as finished/skipped for this user. */
export function setTutorialDone(sub) {
  if (!sub) return;
  try {
    localStorage.setItem(tutorialDoneKey(sub), 'true');
  } catch (error) {
    console.error('dismissals: failed to set tutorial flag', error);
  }
}

/** Reset the first-run welcome tour flag ("Show tutorial" footer link). */
export function clearTutorialDone(sub) {
  if (!sub) return;
  try {
    localStorage.removeItem(tutorialDoneKey(sub));
  } catch (error) {
    console.error('dismissals: failed to clear tutorial flag', error);
  }
}
